import type { SVGProps } from "react";

const COMMON: SVGProps<SVGSVGElement> = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const PATHS: Record<string, string> = {
  dashboard: "M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h7v7h-7z",
  routes: "M3 17h2l3-10h2l3 10h2l3-10",
  map:      "M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2V6z M9 4v16 M15 6v16",
  alerts:   "M12 3a7 7 0 0 0-7 7c0 5-3 6-3 6h20s-3-1-3-6a7 7 0 0 0-7-7z M10 21a2 2 0 0 0 4 0",
  messages: "M21 12a8 8 0 0 1-8 8H7l-4 3v-7a8 8 0 1 1 18-4z",
  panic:    "M12 3l10 18H2L12 3z M12 10v5 M12 18h0",
  reassign: "M3 7h13l-3-3 M21 17H8l3 3",
  sched:    "M3 5h18v16H3z M8 3v4 M16 3v4 M3 10h18",
  stops:    "M12 21s7-7 7-12a7 7 0 0 0-14 0c0 5 7 12 7 12z M12 7a2 2 0 1 1 0 4 2 2 0 0 1 0-4z",
  users:    "M9 9a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7z M2 21c0-3.9 3.1-7 7-7s7 3.1 7 7 M16 7a3 3 0 1 1 0 6 M22 21a6 6 0 0 0-4-5.7",
  signout:  "M15 17l5-5-5-5 M20 12H9 M12 19H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h7",
  settings: "M12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1z",
  firmware: "M12 3v9 M8 8l4 4 4-4 M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4",
  history:  "M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z M12 8v4l3 2",
  devices:  "M4 5h16v10H4z M2 19h20 M10 9h4",
  // Active trips — a route line linking an origin node to a destination node.
  trips:    "M6 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M18 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M8 17h6a3 3 0 0 0 3-3V9",
  // Timetable — calendar grid with a row of slots.
  schedules:"M3 5h18v16H3z M8 3v4 M16 3v4 M3 10h18 M8 14h3 M14 14h2",
  // Network — a hub linked out to three spoke nodes.
  network:  "M12 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4z M5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M19 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M12 8v3 M11 12l-4 3 M13 12l4 3",
  // GTFS — a feed file (page with folded corner + data lines).
  gtfs:     "M6 3h9l3 3v15H6z M14 3v5h4 M9 13h6 M9 17h4",
  // Odometer — a gauge dial with its needle swung right.
  odometer: "M3 17a9 9 0 1 1 18 0 M12 17l5-5",
  // Reports — a bar chart.
  reports:  "M4 20h16 M7 16v-5 M12 16V9 M17 16v-8",
  // Route map — a winding line strung with station stops.
  routeMap: "M3 19c4-6 8 6 12 0s3-13 6-13 M3 19a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8z M12.5 16.5a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8z M21 6a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8z",
};

export type IconName = keyof typeof PATHS | string;

export default function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  const d = PATHS[name];
  if (!d) return null;
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} {...COMMON}>
      {d.split(/(?=[ML]\s*\d)|(?=\s[ML]\s)/).map((seg, i) =>
        seg.trim() ? <path key={i} d={seg.trim()} /> : null
      )}
    </svg>
  );
}
